import {
  CreateButton,
  DeleteButton,
  List,
  NumberField,
  SaveButton,
  TextField,
  getDefaultSortOrder,
  useSelect,
  useTable,
} from "@refinedev/antd";

// It is recommended to use explicit import as seen below to reduce bundle size.
// import { IconName } from "@ant-design/icons";
import * as Icons from "@ant-design/icons";

import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Form,
  FormProps,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";

import { CrudFilters, IResourceComponentsProps, getDefaultFilter, useList, useModal } from "@refinedev/core";
import { INoticesMariners } from "interfaces";
import React from "react";

export const NoticesMarinersList: React.FC<IResourceComponentsProps> = () => {
  const [record, setRecord] = React.useState<INoticesMariners>();
  const [countrySelected, setCountrySelected] = React.useState<boolean>(false);
  const [country, setCountry] = React.useState<string>("");

  const { tableProps, sorters, tableQuery: { isLoading }, searchFormProps, filters } =
    useTable<INoticesMariners>({
      resource: "notices-mariners",
      syncWithLocation: false,

      onSearch: (params: any) => {
        const filters: CrudFilters = [];
        const { title, country, weekNumber, year } = params;

        filters.push({
          field: "country",
          operator: "eq",
          value: country ? country : undefined,
        });

        filters.push({
          field: "title",
          operator: "contains",
          value: title ? title : undefined,
        });

        filters.push({
          field: "weekNumber",
          operator: "eq",
          value: weekNumber ? weekNumber : undefined,
        });

        filters.push({
          field: "year",
          operator: "eq",
          value: year ? year : undefined,
        });

        // filters.push({
        //   field: "issueDate",
        //   operator: "gte",
        //   value: issueDate
        //     ? issueDate[0].startOf("day").toISOString()
        //     : undefined,
        // });

        // filters.push({
        //   field: "issueDate",
        //   operator: "lte",
        //   value: issueDate
        //     ? issueDate[1].startOf("day").toISOString()
        //     : undefined,
        // });

        return filters;
      },

      sorters: {
        initial: [
          {
            field: "id",
            order: "desc",
          },
        ]
      }
    });
  const { show, visible, close } = useModal();

  const columns: ColumnsType<INoticesMariners> = [
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      width: 150,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("country", sorters),
      sorter: true,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("title", sorters),
      sorter: true,
    },
    {
      title: "Week Number",
      dataIndex: "weekNumber",
      key: "weekNumber",
      width: 120,
      className: "wrap-text",
      render: (value) => <NumberField value={value} />,
      defaultSortOrder: getDefaultSortOrder("weekNumber", sorters),
      sorter: true,
    },
    {
      title: "Year",
      dataIndex: "year",
      key: "year",
      width: 100,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("year", sorters),
      sorter: true,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<Icons.DownloadOutlined />}
            href={record?.storageUrl}
            download={true}
          />
          <Button
            size="small"
            icon={<Icons.FilePdfOutlined />}
            download={false}
            onClick={() => {
              setRecord(record);
              show();
            }}
          />
          <DeleteButton
            hideText
            size="small"
            recordItemId={record.id}
          />
        </Space>
      ),
    },
  ];

  function handleCountrySelection(value: string) {
    setCountrySelected(true);
    setCountry(value);
    searchFormProps?.form?.submit();
    searchFormProps?.form?.resetFields(["title", "weekNumber", "year"]);

    //special case as "issueDate" is not reset with form.resetFields() above
    // searchFormProps?.form?.setFieldsValue({ issueDate: undefined });
  }

  return (
    <>
      <List
        breadcrumb={false}
        headerProps={{
          // onBack: () => window.history.back(),
          title: "Digital Bookshelf",
          subTitle: "notices to mariners",
          tags: isLoading ? (
            <Tag color="orange">loading</Tag>
          ) : (
            <Tag color="green">live</Tag>
          ),
          extra: (
            <React.Fragment>
              <Space>
                <CreateButton />
                {/* {countrySelected ? (
                  <Button
                    size="middle"
                    icon={<Icons.FilePdfOutlined />}
                    target="_blank"
                    href={`${process.env.REACT_APP_GCS_FLAG_CIRCULAR_URL || 'https://storage.googleapis.com/emr_flag_circular_test_v1'}/index/10000%20${country}.pdf`}
                    download={false}
                  >
                    View Index
                  </Button>
                ) : (
                  <></>
                )} */}
              </Space>
            </React.Fragment>
          ),
          ghost: true,
          footer: (
            <Divider plain>
              © {new Date().getFullYear()} e-Mariner NavSolutions
            </Divider>
          ),
        }}
      >
        <Row gutter={[16, 16]}>
          <Col xl={24} lg={24} xs={24}>
            <Card type="inner" title={"Filter"}>
              <Filter
                formProps={searchFormProps}
                filters={filters || []}
                countrySelected={countrySelected}
                onSelect={handleCountrySelection}
              />
            </Card>
          </Col>
          <Col xl={24} xs={24}>
            {countrySelected ? (
              <Table
                {...tableProps}
                columns={columns}
                rowKey="id"
                size="middle"
                scroll={{ x: 1200 }}
                sticky
                pagination={{
                  ...tableProps.pagination,
                  showSizeChanger: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                }}
              />
            ) : (
              <Space
                direction="vertical"
                size="small"
                style={{
                  display: "flex",
                }}
              >
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}></Empty>
                <Alert
                  message="Select Country"
                  description="Please select a country to view the data"
                  type="info"
                  showIcon
                  style={{
                    margin: "auto",
                    width: "40%",
                    padding: "15px",
                    textAlign: "center",
                    // alignItems: "center",
                  }}
                />
              </Space>
            )}
          </Col>
        </Row>
      </List>
      <Modal
        centered
        closeIcon={<Icons.CloseSquareFilled />}
        visible={visible}
        onCancel={close}
        width="80%"
        footer={null}
      >
        <div>
          <embed
            src={record?.embedUrl}
            type="application/pdf"
            width="99%"
            height="700px"
          />
        </div>
      </Modal>
    </>
  );
};

const Filter: React.FC<{
  formProps: FormProps;
  filters: CrudFilters;
  countrySelected: boolean;
  onSelect: any;
}> = (props) => {
  const { formProps, filters, countrySelected, onSelect } = props;

  const { result: data, query: {
    isLoading
  } } = useList<INoticesMariners>({
    resource: "notices-mariners",

    pagination: {
      mode: "off"
    }
  });
  const countries = data?.data ?? [];

  const { selectProps: countrySelectProps } = useSelect<INoticesMariners>({
    resource: "notices-mariners",
    optionLabel: "country",
    optionValue: "country",

    sorters: [
      {
        field: "country",
        order: "asc",
      },
    ],

    pagination: {
      mode: "server",
      pageSize: countries.length || 10
    }
  });

  // const { RangePicker } = DatePicker;

  // const issueDate = React.useMemo(() => {
  //   const start = getDefaultFilter("issueDate", filters, "gte");
  //   const end = getDefaultFilter("issueDate", filters, "lte");

  //   const startFrom = dayjs(start);
  //   const endAt = dayjs(end);

  //   if (start && end) {
  //     return [startFrom, endAt];
  //   }
  //   return undefined;
  // }, [filters]);

  return (
    <Form
      layout="vertical"
      {...formProps}
      initialValues={{
        country: getDefaultFilter("country", filters),
        title: getDefaultFilter("title", filters),
        weekNumber: getDefaultFilter("weekNumber", filters),
        year: getDefaultFilter("year", filters),
        // issueDate,
      }}
    >
      <Row gutter={[10, 0]} align="bottom">
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item
            label={"Country"}
            name="country"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Select
              {...countrySelectProps}
              placeholder="filter by country"
              onSelect={onSelect}
              loading={isLoading}
              //disabled={isLoading}
            />
          </Form.Item>
        </Col>
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item label={"Title"} name="title">
            <Input
              placeholder="filter by title"
              allowClear
              disabled={!countrySelected}
            />
          </Form.Item>
        </Col>
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item label={"Week Number"} name="weekNumber">
            <Input
              placeholder="filter by week number"
              allowClear
              disabled={!countrySelected}
            />
          </Form.Item>
        </Col>
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item label={"Year"} name="year">
            <Input
              placeholder="filter by year"
              allowClear
              disabled={!countrySelected}
            />
          </Form.Item>
        </Col>
        <Col xl={24} md={8} sm={12} xs={24}>
          <Form.Item
            style={{
              margin: "auto",
              width: "25%",
            }}
          >
            <SaveButton
              disabled={!countrySelected}
              onClick={formProps.form?.submit}
              size="large"
              block
            >
              Filter
            </SaveButton>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
